import { getMessage as getLangPack, getLocale, replaceVar, retryWithFibonacci, onlyCallOnceInOneTick } from '@web3/utils';
import { PropsWithChildren, createContext, createElement, useCallback, useContext, useEffect, useMemo } from 'react';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';
import { isBrowser } from '../utils/trick';
import { i18nProjectName, rtlLocales } from '../utils/constants';

type State = {
  locale: string;
  modalVisible: boolean;
  messageMap: Record<string, Record<string, string>>;
};

const compute = ({ locale }: State) => {
  return {
    isRTL: rtlLocales.includes(locale),
  };
};
type Computed = ReturnType<typeof compute>;

const maxRetry = 3;

class I18nMessageStore extends EventBasedStore<State, Computed> {
  constructor() {
    super(
      {
        locale: '',
        modalVisible: false,
        messageMap: {},
      },
      compute,
      () => {
        this.setLocale(getLocale());
        if (isBrowser) {
          window.addEventListener('popstate', () => {
            this.setLocale(getLocale());
          });
        }
        this.getLangPack(i18nProjectName);

        if (isBrowser) {
          this.on(
            'update',
            onlyCallOnceInOneTick(({ isRTL }: any) => {
              const htmlDom = document.querySelector('html');
              if (!htmlDom) {
                return;
              }
              if (isRTL) {
                htmlDom.setAttribute('dir', 'rtl');
              } else {
                htmlDom.setAttribute('dir', 'ltr');
              }
            }),
          );
        }
      },
    );
  }

  async setLocale(locale: string) {
    if (!locale) {
      return;
    }
    const { state } = this;
    if (state.locale !== locale) {
      const langProjects = Object.keys(state.messageMap);
      this.setState({
        locale,
        messageMap: {},
      });
      langProjects.forEach((project) => this.getLangPack(project));
      return;
    }
  }

  private _loadingMap = new Map<string, Promise<Record<string, string>>>();
  async getLangPack(project?: string, locale = this.state.locale) {
    if (!project) {
      return {};
    }
    // 获取的语言包在当前store 里有，就直接返回
    if (this.state.locale === locale && this.state.messageMap[project]) {
      return this.state.messageMap[project];
    }

    // 缓存请求
    const key = [project, locale].join('-');

    let req = this._loadingMap.get(key);
    if (!req) {
      req = retryWithFibonacci(() => getLangPack(project, locale), maxRetry);
      this._loadingMap.set(key, req);
    }
    const msgs = await req;

    if (this.state.locale === locale) {
      this.setState({
        messageMap: {
          ...this.state.messageMap,
          [project]: msgs,
        },
      });
    }
    return msgs;
  }

  translate(project: string, key: string, context?: Record<string, string>) {
    const { messageMap } = this.state;
    const messages = messageMap[project];
    if (!messages) {
      i18nMessageStore.getLangPack(project);
      return key;
    }
    const template = messages[key];
    if (!template) {
      console.warn(`missing translation for ${key}`);
      return key;
    }
    return replaceVar(template, context, { doubleBrace: false, keepVarName: true });
  }

  translateFn(project: string) {
    return this.translate.bind(this, project);
  }

  hasMessage(project: string): boolean {
    return Boolean(this.state.messageMap[project]);
  }

  showModal() {
    this.setState({
      modalVisible: true,
    });
  }
  hideModal() {
    this.setState({
      modalVisible: false,
    });
  }
}

export const i18nMessageStore = new I18nMessageStore();

const I18nProjectContext = createContext<string>(i18nProjectName);

export const I18nProjectProvider = ({ workingProject, children }: PropsWithChildren<{ workingProject }>) => {
  useEffect(() => {
    i18nMessageStore.getLangPack(workingProject);
  }, [workingProject]);

  return createElement(I18nProjectContext.Provider, { value: workingProject, children });
};

export const useLocale = () => {
  const state = i18nMessageStore.useStoreState();
  const workingProject = useContext(I18nProjectContext);

  const { messageMap } = state;

  const t = useCallback(
    (key: string, context?: Record<string, string> | string, project = workingProject) => {
      if (typeof context === 'string') {
        project = context;
        context = undefined;
      }
      return i18nMessageStore.translate(project, key, context);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workingProject, messageMap],
  );

  const hasMessage = useCallback((project = workingProject) => i18nMessageStore.hasMessage(project), [workingProject]);

  const actions = useMemo(() => pickStoreActions(i18nMessageStore, ['hideModal', 'showModal', 'setLocale', 'getLangPack']), []);
  const messages = messageMap[workingProject];
  return {
    ...state,
    ...actions,
    t,
    hasMessage,
    messages,
    workingProject,
  };
};
